import { LoadingOutlined } from "@ant-design/icons"
import { Expense, ExpensePaymentFlowInformationEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Button, Empty, Table, Tooltip } from "antd"
import { ColumnsType } from "antd/lib/table"
import AmountColumn from "Components/expenseAmountColumn"
import ExpenseTagColumn from "Components/expenseTagColumn"
import { ErrorTable } from "Components/layout/errorTable"
import Loading from "Components/loading"
import NoSearchDataFound from "Components/NoSearchDataFound"
import RequestColumn from "Components/requestColumn"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useLoadingExpenseIds, useSelectedExpenses } from "Shared/context/selectedExpenses.context"
import { useCompany } from "Shared/hooks/company.hooks"
import { useFetchTable, useTable, useTableFilterAndSearchLoading, useTableSearchString } from "Shared/hooks/table.hooks"
import { useCreditors } from "Shared/hooks/vendor.hooks"
import { UserService } from "Shared/services"
import { setExpense } from "Shared/store/actions/expense/expenseActions"
import { refetchTable, updateTableOnChange } from "Shared/store/actions/tables/tableActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { getPopupAnchor } from "Shared/utils"
import { insertIf } from "Shared/utils/helpers"
import { getSortOrderForColumn } from "Shared/utils/table.utils"
import { isExpenseAbleToProceed, markExpenseAsReviewed } from "../expenseUtils"

interface ReviewTabPropInterface {}

const ReviewTab: React.FC<ReviewTabPropInterface> = ({}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()
    const loggedInProfile = UserService.getLoggedInEmployeeProfile()
    const [selectedExpenses, setSelectedExpenses] = useSelectedExpenses()
    const [loadingExpenseIds, setLoadingExpenseIds] = useLoadingExpenseIds()
    const reviewTable = useTable<Expense>(TablesEnum.REVIEW_REQUESTS)
    const tabLoading = useTableFilterAndSearchLoading(TablesEnum.REVIEW_REQUESTS)
    const { taggingEnabled } = useCompany()
    const creditors = useCreditors(true, true, true)
    const showTags = taggingEnabled && reviewTable.data.docs.some((expense) => expense?.expenseTags?.length)

    const searchString = useTableSearchString(TablesEnum.REVIEW_REQUESTS)

    useFetchTable(TablesEnum.REVIEW_REQUESTS)

    const renderActionColumn = (data: Expense) => {
        const creditor = creditors.find((c) => c.id === data.vendor)
        const isDisabled = !isExpenseAbleToProceed(data, creditor) && data.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY
        const isVendorApproved = creditor?.type === VendorTypeEnum.USER || (creditor?.source as Vendor)?.isApproved

        return (
            <div className="btn-wrapper">
                <Tooltip
                    // Need to explicitly set the width to avoid being pushed back by the next column
                    overlayStyle={{ width: 500 }}
                    getPopupContainer={getPopupAnchor()}
                    title={
                        isDisabled ? (
                            <div>{t("tooltips:expenses.not_all_information_provided")}</div>
                        ) : !isVendorApproved ? (
                            <div>{t("tooltips:expenses.vendor_not_approved")}</div>
                        ) : undefined
                    }
                    placement="right"
                >
                    <Button
                        loading={loadingExpenseIds.includes(data.id)}
                        className="mr-auto ml-auto"
                        onClick={(e) => {
                            e.stopPropagation()
                            setLoadingExpenseIds([data.id])
                            markExpenseAsReviewed(data).finally(() => setLoadingExpenseIds([]))
                        }}
                        disabled={isDisabled || !isVendorApproved}
                        type="primary"
                    >
                        <span>{t("action:mark_as_reviewed")}</span>
                    </Button>
                </Tooltip>
            </div>
        )
    }

    const columns: ColumnsType<Expense> = [
        {
            title: t("label:request"),
            key: "expenseNumber",
            align: "left",
            ellipsis: true,
            width: 350,
            ...(loggedInProfile.settings.showExpenseNumbers && {
                sorter: true,
                sortOrder: getSortOrderForColumn(reviewTable, "expenseNumber"),
            }),
            render: (data: any) => <RequestColumn request={data} />,
        },
        ...insertIf(showTags, {
            title: t("label:tag"),
            key: "tag",
            align: "left",
            width: 130,
            render: ({ expenseTags: expenseTagIds }: Expense) => {
                return <ExpenseTagColumn expenseTagIds={expenseTagIds} />
            },
        }),
        {
            title: t("label:action"),
            key: "action",
            align: "center",
            width: 260,
            render: (expense: Expense) => renderActionColumn(expense),
        },
        {
            title: loggedInProfile.settings.showGrossAmount ? t("label:gross_amount") : t("label:net_amount"),
            key: loggedInProfile.settings.showGrossAmount ? "totalGrossPrice" : "totalNetPrice",
            align: "right",
            className: "pr-10",
            width: 200,
            sorter: true,
            sortOrder: getSortOrderForColumn(reviewTable, loggedInProfile.settings.showGrossAmount ? "totalGrossPrice" : "totalNetPrice"),
            render: (data: Expense) => <AmountColumn request={data} showGross={loggedInProfile.settings.showGrossAmount} />,
        },
    ]

    return tabLoading ? (
        <Loading />
    ) : reviewTable.error ? (
        <ErrorTable onTableReload={() => dispatch(refetchTable(TablesEnum.REVIEW_REQUESTS))} isLoading={reviewTable.isFetching} />
    ) : reviewTable.data.totalDocs === 0 && !reviewTable.isFetching ? (
        searchString ? (
            <div className="animation-appear">
                <NoSearchDataFound />
            </div>
        ) : (
            <Empty
                className="animation-appear"
                image={`./icons/empty_table.svg`}
                imageStyle={{
                    height: 150,
                    marginRight: "auto",
                    marginLeft: "auto",
                    marginTop: "15%",
                    marginBottom: "40px",
                    display: "inline-block",
                }}
                description={<span className="text-lg font-bold mt-2 mb-0">{t("info:empty_state.requests.title")}</span>}
            >
                <p>{t("info:empty_state.review.message")}</p>
            </Empty>
        )
    ) : (
        <div className="overflow-auto mb-24 p-2 animation-appear">
            <ul className="card-list">
                <Table
                    rowKey={(record) => record.id}
                    dataSource={reviewTable.data.docs}
                    columns={columns}
                    rowSelection={{
                        type: "checkbox",
                        renderCell: (_value, _record, _index, originNode) => (
                            <div onClick={(e) => e.stopPropagation()} className="bigger-hitbox-checkbox">
                                {originNode}
                            </div>
                        ),
                        selectedRowKeys: selectedExpenses.map((record) => record.id),
                        onChange: (_selectedRowKeys, selectedRows) => setSelectedExpenses(selectedRows),
                        onSelect: (record, _selectedRow) => setSelectedExpenses([...selectedExpenses, record]),
                        getCheckboxProps: (data: Expense) => {
                            const creditor = creditors.find((c) => c.id === data.vendor)
                            const isVendorApproved = creditor?.type === VendorTypeEnum.USER || (creditor?.source as Vendor)?.isApproved

                            return {
                                disabled:
                                    (!isExpenseAbleToProceed(data, creditor) && data.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY) ||
                                    !isVendorApproved,
                            }
                        },
                    }}
                    pagination={{
                        current: reviewTable.data.page,
                        pageSize: reviewTable.data.limit,
                        total: reviewTable.data.totalDocs,
                        position: ["bottomRight"],
                        showSizeChanger: true,
                        hideOnSinglePage: false,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                    }}
                    onChange={updateTableOnChange(TablesEnum.REVIEW_REQUESTS, reviewTable.data)}
                    loading={{
                        spinning: reviewTable.isFetching,
                        indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                    }}
                    onRow={(record: Expense, _rowIndex) => {
                        return {
                            onClick: (_event) => {
                                dispatch(setExpense(record))
                                history.push(`/expenses/${record.id}`)
                            },
                        }
                    }}
                />
            </ul>
        </div>
    )
}

export default ReviewTab
